import React, { ReactNode, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';

import { ClientPortalConfigurationsSelector } from 'store/clientPortalChats';
import { ThunkGetClientPortalConfigurations } from 'store/clientPortalChats/thunks';
import { ConfigurationSelector } from 'store/configuration';
import { MyPricePlanSelector } from 'store/pricePlan/selectors';
import { ThunkGetPricePlans } from 'store/pricePlan/thunks';
import { hasWhatsAppSettingsSelector } from 'store/whatsAppMessaging';
import { ThunkGetWhatsAppSettings } from 'store/whatsAppMessaging/thunks';

import { ReactComponent as ChatsIcon } from 'assets/icons/chats.svg';
import { ReactComponent as TechVerificationIcon } from 'assets/icons/person_search.svg';
import { ReactComponent as EndUserVerificationIcon } from 'assets/icons/shield_person.svg';
import { ReactComponent as SMSIcon } from 'assets/icons/sms.svg';
import { ReactComponent as SecureDatSharingIcon } from 'assets/icons/sync.svg';
import { ReactComponent as TeamsIcon } from 'assets/icons/teams_flat.svg';
import { ReactComponent as TicketIcon } from 'assets/icons/ticket.svg';
import { ReactComponent as WhatsappIcon } from 'assets/icons/whatsapp.svg';

import { Integrations } from 'constants/integrations';
import { Pages } from 'constants/pages';
import { PricePlans } from 'constants/pricePlans';

interface IMenuItem {
  text: string;
  icon: ReactNode;
  page: Pages;
  allowedPsa: Integrations[];
  screens: string[];
  pricePlans: PricePlans[];
}
const allIntegrations = [
  Integrations.ConnectWise,
  Integrations.Halo,
  Integrations.Autotask,
  Integrations.Kaseya,
  Integrations.Syncro,
  Integrations.ServiceNow,
  Integrations.SuperOps,
  Integrations.Zendesk,
  Integrations.Microsoft,
  Integrations.ImportIntegration,
  Integrations.FreshService,
];

const allPricePlans = [PricePlans.Pro, PricePlans.VerificationPlus, PricePlans.Free];

export const menuItems: IMenuItem[] = [
  {
    text: 'End User Verification',
    icon: <EndUserVerificationIcon />,
    page: Pages.EndUserVerification,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
  },
  {
    text: 'SMS Messaging',
    icon: <SMSIcon />,
    page: Pages.SMSMessaging,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'WhatsApp Messaging',
    icon: <WhatsappIcon />,
    page: Pages.WhatsAppMessaging,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'project_ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'Client Portal Chats',
    icon: <ChatsIcon />,
    page: Pages.ClientPortalChats,
    allowedPsa: [Integrations.ConnectWise, Integrations.Halo, Integrations.Autotask],
    screens: ['ticket'],
    pricePlans: [PricePlans.Pro],
  },
  {
    text: 'Secure Data Sharing',
    icon: <SecureDatSharingIcon />,
    page: Pages.SecureDataSharing,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: allPricePlans,
  },
  {
    text: 'Technician Verification',
    icon: <TechVerificationIcon />,
    page: Pages.TechnicianVerification,
    allowedPsa: allIntegrations,
    screens: ['ticket', 'contact', 'project_ticket'],
    pricePlans: [PricePlans.VerificationPlus, PricePlans.Pro],
  },
  {
    text: 'Quick Ticket',
    icon: <TicketIcon />,
    page: Pages.QuickTicket,
    allowedPsa: [Integrations.ConnectWise],
    screens: ['contact'],
    pricePlans: allPricePlans,
  },
  {
    text: 'MS Teams Chats',
    icon: <TeamsIcon />,
    page: Pages.MSTeamsChats,
    allowedPsa: [Integrations.ConnectWise],
    screens: ['ticket'],
    pricePlans: allPricePlans,
  },
];

export const useAllowedMenuItems = (): IMenuItem[] => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(ConfigurationSelector);
  const myPlan = useAppSelector(MyPricePlanSelector);
  const clientPortalConfigs = useAppSelector(ClientPortalConfigurationsSelector);
  const hasWhatsAppSettings = useAppSelector(hasWhatsAppSettingsSelector);

  const psaFiltered = useMemo(
    () =>
      menuItems
        .filter(i => config?.psa && i.allowedPsa.includes(config?.psa))
        .filter(i => (config?.screen ? i.screens.includes(config?.screen) : true)),
    [config],
  );

  const haveClientPortalConfig = useMemo(() => {
    return clientPortalConfigs.some(i => i.crmId === config?.psaId);
  }, [clientPortalConfigs, config?.psaId]);

  useEffect(() => {
    if (config?.psaId) {
      Promise.all([
        dispatch(ThunkGetPricePlans()),
        dispatch(ThunkGetClientPortalConfigurations()),
        dispatch(ThunkGetWhatsAppSettings()),
      ]);
    }
  }, [config?.psaId, dispatch]);

  // if USER don't have subscription don't show any items
  if (!myPlan?.isSubscriptionEnabled) {
    return [];
  }

  // if USER has DEMO or OBSOLETE subscription return all
  if (myPlan?.isDemoSubscription ?? myPlan?.isSubscriptionObsolete) {
    return psaFiltered;
  }

  return psaFiltered
    .filter(i => {
      if (myPlan.name) {
        return i.pricePlans.includes(myPlan?.name);
      }
      return true;
    })
    .filter(i => (i.page === Pages.ClientPortalChats ? haveClientPortalConfig : true))
    .filter(i => (i.page === Pages.WhatsAppMessaging ? hasWhatsAppSettings : true))
    .filter(i => i.page !== Pages.MSTeamsChats);
};
